import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getRequest, postRequest } from "../../utils/httpRequest";
import EscrowTab from "./EscrowTab";
import OwlPayReconciliationCard from "./OwlPayReconciliationCard";

// ============================================================================
// Styles and Colors (Matching the dashboard's design system)
// ============================================================================
const colors = {
    primary: "#2e3b61",
    primaryLight: "#3d4d7a",
    accent: "#e58734",
    accentDark: "#d77a2a",
    success: "#28a745",
    successDark: "#218838",
    danger: "#dc3545",
    dangerDark: "#c82333",
    warning: "#ffc107",
    warningText: "#856404",
    warningBg: "#fff3cd",
    info: "#17a2b8",
    infoDark: "#138496",
    white: "#fff",
    gray100: "#f8f9fa",
    gray200: "#f0f2f5",
    gray300: "#e0e0e0",
    gray400: "#ccc",
    gray500: "#999",
    gray600: "#666",
    gray700: "#333",
    border: "#dcdfe6",
};

const gradients = {
    primary: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
    accent: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
    success: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
    danger: `linear-gradient(135deg, ${colors.danger} 0%, ${colors.dangerDark} 100%)`,
    info: `linear-gradient(135deg, ${colors.info} 0%, ${colors.infoDark} 100%)`,
    warning: `linear-gradient(135deg, #e6a23c 0%, #cf8e28 100%)`,
    neutral: `linear-gradient(135deg, ${colors.gray600} 0%, ${colors.gray700} 100%)`,
};

// ============================================================================
// Main Component
// ============================================================================
const LiquidityPoolReconciliation = () => {
    // Active Tab state
    const [activeTab, setActiveTab] = useState("reserves");

    // Data state
    const [reserves, setReserves] = useState([]);
    const [suspenseTxs, setSuspenseTxs] = useState([]);
    const [stuckTxs, setStuckTxs] = useState([]);

    // Loading & error states
    const [loadingReserves, setLoadingReserves] = useState(true);
    const [loadingSuspense, setLoadingSuspense] = useState(true);
    const [loadingStuck, setLoadingStuck] = useState(true);
    // Distinct from "no rows": a failed read must never be presentable as a
    // clean reconciliation. See the Reserve Mismatches card below.
    const [reservesError, setReservesError] = useState(false);

    const [error, setError] = useState(null);

    // Modal state
    const [actionModal, setActionModal] = useState({
        isOpen: false,
        type: "", // 'force_resolve' or 'confirm_deposit'
        tx: null,
    });
    const [resolutionStatus, setResolutionStatus] = useState("FAILED");
    const [actionReason, setActionReason] = useState("");
    const [actionSubmitting, setActionSubmitting] = useState(false);

    // Toast notification state
    const [toast, setToast] = useState(null);

    const showToast = useCallback((message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // ========================================================================
    // Data Fetching
    // ========================================================================
    const fetchReserves = useCallback(async () => {
        setLoadingReserves(true);
        try {
            const res = await getRequest("/admin/liquidity_pool_reconciliation/reserves?limit=100");
            if (res.success && res.data) {
                setReserves(res.data.rows || []);
                setReservesError(false);
            } else {
                throw new Error(res.error || "Failed to fetch user reserves");
            }
        } catch (err) {
            console.error(err);
            // The previous rows are dropped on purpose. Leaving stale numbers on
            // screen under a headline that claims to reconcile the live ledger is
            // worse than showing nothing.
            setReserves([]);
            setReservesError(true);
            showToast("Failed to fetch reserves: " + (err.message || "Unknown error"), "danger");
        } finally {
            setLoadingReserves(false);
        }
    }, [showToast]);

    const fetchSuspenseTxs = useCallback(async () => {
        setLoadingSuspense(true);
        try {
            const res = await getRequest("/admin/liquidity_pool_reconciliation/transactions?userId=SUSPENSE&limit=100");
            if (res.success && res.data) {
                setSuspenseTxs(res.data.data || []);
            } else {
                throw new Error(res.error || "Failed to fetch suspense transactions");
            }
        } catch (err) {
            console.error(err);
            showToast("Failed to fetch suspense transactions: " + (err.message || "Unknown error"), "danger");
        } finally {
            setLoadingSuspense(false);
        }
    }, [showToast]);

    const fetchStuckTxs = useCallback(async () => {
        setLoadingStuck(true);
        try {
            const res = await getRequest("/admin/liquidity_pool_reconciliation/transactions?stuck=true&limit=100");
            if (res.success && res.data) {
                setStuckTxs(res.data.data || []);
            } else {
                throw new Error(res.error || "Failed to fetch stuck transactions");
            }
        } catch (err) {
            console.error(err);
            showToast("Failed to fetch stuck transactions: " + (err.message || "Unknown error"), "danger");
        } finally {
            setLoadingStuck(false);
        }
    }, [showToast]);

    const loadAllData = useCallback(() => {
        fetchReserves();
        fetchSuspenseTxs();
        fetchStuckTxs();
    }, [fetchReserves, fetchSuspenseTxs, fetchStuckTxs]);

    useEffect(() => {
        loadAllData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ========================================================================
    // Computed Values / Statistics
    // ========================================================================
    const stats = useMemo(() => {
        let totalReserve = 0;
        let totalMinted = 0;
        let mismatchCount = 0;

        reserves.forEach((r) => {
            const rBal = parseFloat(r.reserveBalance || 0);
            const nBal = parseFloat(r.nativeBalance || 0);
            totalReserve += rBal;
            totalMinted += nBal;
            if (Math.abs(rBal - nBal) > 0.000001) {
                mismatchCount++;
            }
        });

        const suspenseCount = suspenseTxs.length;
        const stuckCount = stuckTxs.length;

        return {
            totalReserve,
            totalMinted,
            mismatchCount,
            suspenseCount,
            stuckCount,
            // How many ledgers were actually compared. Zero comparisons is not
            // zero mismatches, and the cards must not conflate the two.
            comparedCount: reserves.length,
        };
    }, [reserves, suspenseTxs, stuckTxs]);

    // Nothing was summed, so no total can be stated. Kept next to the cards it
    // governs rather than inside `stats`, which is memoised on the rows alone.
    const unreadTotals = reservesError || stats.comparedCount === 0;

    // ========================================================================
    // Modal Actions Handlers
    // ========================================================================
    const openActionModal = (type, tx) => {
        setResolutionStatus("FAILED");
        setActionReason("");
        setActionModal({
            isOpen: true,
            type,
            tx,
        });
    };

    const closeActionModal = () => {
        setActionModal({
            isOpen: false,
            type: "",
            tx: null,
        });
    };

    const handleActionSubmit = async (e) => {
        e.preventDefault();
        const { type, tx } = actionModal;
        if (!tx) return;

        setActionSubmitting(true);
        try {
            let res;
            if (type === "force_resolve") {
                if (!actionReason.trim()) {
                    showToast("Please provide a justification reason", "warning");
                    setActionSubmitting(false);
                    return;
                }
                res = await postRequest("/admin/liquidity_pool_reconciliation/force_resolve", {
                    id: tx.id,
                    resolution: resolutionStatus,
                    reason: actionReason,
                });
            } else if (type === "confirm_deposit") {
                res = await postRequest("/admin/liquidity_pool_reconciliation/confirm_deposit", {
                    id: tx.id,
                });
            }

            if (res && res.success) {
                showToast(
                    type === "force_resolve"
                        ? "Transaction force resolved successfully."
                        : "Deposit manually confirmed.",
                    "success"
                );
                closeActionModal();
                // Refresh data
                loadAllData();
            } else {
                throw new Error((res && res.error) || "Action execution failed");
            }
        } catch (err) {
            console.error(err);
            const errData = err.data || {};
            const errMsg = errData.error || err.message || "Request failed";
            showToast("Failed to execute action: " + errMsg, "danger");
        } finally {
            setActionSubmitting(false);
        }
    };

    // ========================================================================
    // Render Functions
    // ========================================================================
    const formatAmount = (val) => {
        const num = parseFloat(val);
        if (isNaN(num)) return "0.00";
        return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleString();
    };

    return (
        <div style={{ fontFamily: "Inter, Roboto, system-ui, sans-serif" }}>
            {/* Styles inject */}
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .reconcile-tab-btn {
                    padding: 12px 24px;
                    border: none;
                    background: transparent;
                    color: ${colors.gray600};
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.2s ease;
                    /* the admin flat skin embosses <button> text with a shadow;
                       clear it so the tab labels render crisp */
                    text-shadow: none;
                    -webkit-font-smoothing: antialiased;
                }
                .reconcile-tab-btn:hover {
                    color: ${colors.primary};
                }
                .reconcile-tab-btn.active {
                    color: ${colors.primary};
                    border-bottom-color: ${colors.accent};
                }
                .reconcile-card {
                    background: ${colors.white};
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                    border: 1px solid ${colors.border};
                    overflow: hidden;
                    margin-bottom: 24px;
                }
                .reconcile-table {
                    width: 100%;
                    border-collapse: collapse;
                    text-align: left;
                    font-size: 13px;
                }
                .reconcile-table th {
                    background: ${colors.gray100};
                    color: ${colors.primary};
                    font-weight: 700;
                    padding: 12px 16px;
                    border-bottom: 2px solid ${colors.border};
                }
                .reconcile-table td {
                    padding: 12px 16px;
                    border-bottom: 1px solid ${colors.border};
                    vertical-align: middle;
                    color: ${colors.gray700};
                }
                .reconcile-table tr:hover td {
                    background-color: ${colors.gray100};
                }
                .action-btn {
                    padding: 6px 12px;
                    font-size: 12px;
                    font-weight: 600;
                    border: 1px solid transparent;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    margin-right: 6px;
                    display: inline-flex;
                    align-items: center;
                }
                .action-btn-danger {
                    background: #fef0f0;
                    color: ${colors.danger};
                    border-color: #fde2e2;
                }
                .action-btn-danger:hover {
                    background: ${colors.danger};
                    color: ${colors.white};
                }
                .action-btn-success {
                    background: #f0f9eb;
                    color: ${colors.success};
                    border-color: #e1f3d8;
                }
                .action-btn-success:hover {
                    background: ${colors.success};
                    color: ${colors.white};
                }
                .reconcile-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    display: inline-block;
                }
                .toast-alert {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    padding: 14px 20px;
                    border-radius: 6px;
                    color: ${colors.white};
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    font-weight: 600;
                    font-size: 14px;
                    transition: all 0.3s ease;
                }
            ` }} />

            {/* Toast notification */}
            {toast && (
                <div className="toast-alert" style={{
                    background: toast.type === "success" ? colors.success : toast.type === "warning" ? colors.warning : colors.danger
                }}>
                    {toast.message}
                </div>
            )}

            {/* Page Header */}
            <div style={{
                background: gradients.primary,
                padding: "24px 32px",
                borderRadius: "8px 8px 0 0",
                color: colors.white,
                marginBottom: "24px",
                borderBottom: `3px solid ${colors.accent}`
            }}>
                <h1 style={{ margin: "0 0 8px 0", fontSize: "26px", fontWeight: 700 }}>
                    Reconciliation Dashboard
                </h1>
                <p style={{ margin: 0, fontSize: "14px", color: "rgba(255, 255, 255, 0.8)" }}>
                    Reconcile reserve ledger balances with on-chain supply, resolve suspense items, and override stuck transactions.
                </p>
            </div>

            {/* Both headline totals are sums over the reserve rows, so they are
                only meaningful once some rows exist. */}
            {/* Stats Cards Row */}
            <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap" }}>
                {/* Total Reserves */}
                <div className="reconcile-card" style={{ flex: 1, minWidth: "200px", padding: "16px 20px", background: gradients.primary, color: colors.white, border: "none" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>
                        Total Reserves Ledger
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 700, margin: "8px 0" }}>
                        {unreadTotals ? "-" : `$${formatAmount(stats.totalReserve)}`}
                    </div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                        {unreadTotals
                            ? (reservesError ? "Could not be read, not the same as zero" : "No reserve ledgers loaded")
                            : "Sum of all reserve ledger balances"}
                    </div>
                </div>

                {/* Total Minted Supply */}
                <div className="reconcile-card" style={{ flex: 1, minWidth: "200px", padding: "16px 20px", background: gradients.info, color: colors.white, border: "none" }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>
                        Total Minted Supply
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 700, margin: "8px 0" }}>
                        {unreadTotals ? "-" : `$${formatAmount(stats.totalMinted)}`}
                    </div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                        {unreadTotals
                            ? (reservesError ? "Could not be read, not the same as zero" : "No reserve ledgers loaded")
                            : "Total NGNb/USDb supply minted on Hedera"}
                    </div>
                </div>

                {/* Reserves Mismatches.
                    Three states, not two. With no ledgers loaded, "0 mismatches"
                    is arithmetic on an empty set, and colouring that green told
                    accounting the books balanced when nothing had been read at
                    all. A pass has to be earned by an actual comparison. */}
                <div className="reconcile-card" style={{
                    flex: 1, minWidth: "200px", padding: "16px 20px",
                    background: reservesError
                        ? gradients.danger
                        : stats.comparedCount === 0
                            ? gradients.neutral
                            : stats.mismatchCount > 0 ? gradients.danger : gradients.success,
                    color: colors.white, border: "none"
                }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>
                        Reserve Mismatches
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 700, margin: "8px 0" }}>
                        {reservesError || stats.comparedCount === 0 ? "-" : stats.mismatchCount}
                    </div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                        {reservesError
                            ? "⚠️ Could not read the reserve ledgers, unknown, not balanced"
                            : stats.comparedCount === 0
                                ? "No reserve ledgers to compare, nothing has been checked"
                                : stats.mismatchCount > 0
                                    ? `⚠️ Out of sync user reserve ledgers (of ${stats.comparedCount} checked)`
                                    : `✅ All ${stats.comparedCount} reserve ledgers match minted supplies`}
                    </div>
                </div>

                {/* Suspense Count */}
                <div className="reconcile-card" style={{
                    flex: 1, minWidth: "200px", padding: "16px 20px",
                    background: stats.suspenseCount > 0 ? gradients.warning : gradients.success,
                    color: colors.white, border: "none"
                }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>
                        Suspense Transactions
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 700, margin: "8px 0" }}>
                        {stats.suspenseCount}
                    </div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                        {stats.suspenseCount > 0 ? "⚠️ Payments assigned to SUSPENSE user" : "✅ No suspense transactions"}
                    </div>
                </div>

                {/* Stuck Count */}
                <div className="reconcile-card" style={{
                    flex: 1, minWidth: "200px", padding: "16px 20px",
                    background: stats.stuckCount > 0 ? gradients.danger : gradients.success,
                    color: colors.white, border: "none"
                }}>
                    <div style={{ fontSize: "12px", textTransform: "uppercase", color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>
                        Stuck Payments (Auto-Poll Mismatch)
                    </div>
                    <div style={{ fontSize: "24px", fontWeight: 700, margin: "8px 0" }}>
                        {stats.stuckCount}
                    </div>
                    <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                        {stats.stuckCount > 0 ? "⚠️ Stuck transactions (> 5 polls)" : "✅ No stuck transactions"}
                    </div>
                </div>
            </div>

            {/* OwlPay partner-fee reconciliation (internal cost/margin view) */}
            <OwlPayReconciliationCard colors={colors} showToast={showToast} />

            {/* Navigation Tab Bar */}
            <div style={{ display: "flex", borderBottom: `2px solid ${colors.border}`, marginBottom: "20px" }}>
                <button
                    className={`reconcile-tab-btn ${activeTab === "reserves" ? "active" : ""}`}
                    onClick={() => setActiveTab("reserves")}
                >
                    Reserves Ledger Reconciliation ({reserves.length})
                </button>
                <button
                    className={`reconcile-tab-btn ${activeTab === "suspense" ? "active" : ""}`}
                    onClick={() => setActiveTab("suspense")}
                >
                    Suspense Accounts ({suspenseTxs.length})
                </button>
                <button
                    className={`reconcile-tab-btn ${activeTab === "stuck" ? "active" : ""}`}
                    onClick={() => setActiveTab("stuck")}
                >
                    Auto-Polling Stuck Payments ({stuckTxs.length})
                </button>
                <button
                    className={`reconcile-tab-btn ${activeTab === "escrow" ? "active" : ""}`}
                    onClick={() => setActiveTab("escrow")}
                >
                    Escrow Diagnostics
                </button>
                <button
                    className="reconcile-tab-btn"
                    onClick={loadAllData}
                    style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", color: colors.primary }}
                >
                    🔄 Refresh Dashboard
                </button>
            </div>

            {/* Tab Contents */}
            <div className="reconcile-card">
                {activeTab === "reserves" && (
                    <div>
                        {loadingReserves ? (
                            <div style={{ padding: "40px", textAlign: "center" }}>
                                <div style={{ width: "30px", height: "30px", border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: "50%", animation: "spin 1s linear infinite", display: "inline-block" }}></div>
                                <div style={{ marginTop: "12px", color: colors.gray600 }}>Loading reserves...</div>
                            </div>
                        ) : reserves.length === 0 ? (
                            <div style={{ padding: "40px", textAlign: "center", color: colors.gray500 }}>
                                No user reserve balances found.
                            </div>
                        ) : (
                            <table className="reconcile-table">
                                <thead>
                                    <tr>
                                        <th>User ID</th>
                                        <th>Brale Account ID</th>
                                        <th>User Hedera Wallet</th>
                                        <th>Reserve Balance</th>
                                        <th>Minted Supply</th>
                                        <th>Status</th>
                                        <th>Last Synced At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reserves.map((row, idx) => {
                                        const rBal = parseFloat(row.reserveBalance || 0);
                                        const nBal = parseFloat(row.nativeBalance || 0);
                                        const hasMismatch = Math.abs(rBal - nBal) > 0.000001;
                                        return (
                                            <tr key={idx}>
                                                <td style={{ fontWeight: "600" }}>{row.userId}</td>
                                                <td>{row.braleAccountId || <span style={{ color: colors.gray500 }}>None</span>}</td>
                                                <td style={{ fontFamily: "monospace" }}>{row.userHederaAddress || <span style={{ color: colors.gray500 }}>None</span>}</td>
                                                <td style={{ fontWeight: "700" }}>${formatAmount(row.reserveBalance)}</td>
                                                <td style={{ fontWeight: "700", color: hasMismatch ? colors.danger : colors.gray700 }}>
                                                    ${formatAmount(row.nativeBalance)}
                                                </td>
                                                <td>
                                                    {hasMismatch ? (
                                                        <span className="reconcile-badge" style={{ background: "#fde2e2", color: colors.danger }}>
                                                            Mismatch
                                                        </span>
                                                    ) : (
                                                        <span className="reconcile-badge" style={{ background: "#e1f3d8", color: colors.success }}>
                                                            Synced
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{formatDate(row.lastSyncedAt)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {activeTab === "suspense" && (
                    <div>
                        {loadingSuspense ? (
                            <div style={{ padding: "40px", textAlign: "center" }}>
                                <div style={{ width: "30px", height: "30px", border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: "50%", animation: "spin 1s linear infinite", display: "inline-block" }}></div>
                                <div style={{ marginTop: "12px", color: colors.gray600 }}>Loading suspense transactions...</div>
                            </div>
                        ) : suspenseTxs.length === 0 ? (
                            <div style={{ padding: "40px", textAlign: "center", color: colors.gray500 }}>
                                No suspense transactions found.
                            </div>
                        ) : (
                            <table className="reconcile-table">
                                <thead>
                                    <tr>
                                        <th>Ref / ID</th>
                                        <th>Direction</th>
                                        <th>Fiat / Crypto Amount</th>
                                        <th>Source / Dest Wallet</th>
                                        <th>Attempts</th>
                                        <th>Status</th>
                                        <th>Created At</th>
                                        <th>Override Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {suspenseTxs.map((tx) => (
                                        <tr key={tx.id}>
                                            <td>
                                                <div style={{ fontWeight: "700" }}>{tx.referenceCode || "N/A"}</div>
                                                <div style={{ fontSize: "11px", color: colors.gray500, fontFamily: "monospace" }}>{tx.id}</div>
                                            </td>
                                            <td>
                                                {tx.direction === "ON_RAMP" ? (
                                                    <span className="reconcile-badge" style={{ background: "#e8f4ff", color: colors.info }}>On Ramp</span>
                                                ) : (
                                                    <span className="reconcile-badge" style={{ background: "#fdf6ec", color: colors.accent }}>Off Ramp</span>
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: "700" }}>{formatAmount(tx.cryptoAmount)} {tx.cryptoToken}</div>
                                                <div style={{ fontSize: "11px", color: colors.gray600 }}>{formatAmount(tx.fiatAmount)} {tx.fiatCurrency}</div>
                                            </td>
                                            <td style={{ fontFamily: "monospace", fontSize: "11px" }}>
                                                {tx.direction === "ON_RAMP" ? (
                                                    <div>Dest: {tx.destinationWalletAddress || "None"}</div>
                                                ) : (
                                                    <div>Source: {tx.sourceWalletAddress || "None"}</div>
                                                )}
                                            </td>
                                            <td>{tx.pollAttempts || 0}</td>
                                            <td>
                                                <span className="reconcile-badge" style={{
                                                    background: tx.status === "SETTLED" ? "#e1f3d8" : tx.status === "FAILED" ? "#fef0f0" : "#fff3cd",
                                                    color: tx.status === "SETTLED" ? colors.success : tx.status === "FAILED" ? colors.danger : colors.warningText
                                                }}>
                                                    {tx.status}
                                                </span>
                                            </td>
                                            <td>{formatDate(tx.createdAt)}</td>
                                            <td>
                                                {tx.status !== "SETTLED" && tx.status !== "FAILED" && tx.status !== "CANCELLED" && tx.status !== "EXPIRED" && (
                                                    <>
                                                        {tx.direction === "ON_RAMP" && (
                                                            <button
                                                                className="action-btn action-btn-success"
                                                                onClick={() => openActionModal("confirm_deposit", tx)}
                                                            >
                                                                Confirm Deposit
                                                            </button>
                                                        )}
                                                        <button
                                                            className="action-btn action-btn-danger"
                                                            onClick={() => openActionModal("force_resolve", tx)}
                                                        >
                                                            Force Resolve
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {activeTab === "stuck" && (
                    <div>
                        {loadingStuck ? (
                            <div style={{ padding: "40px", textAlign: "center" }}>
                                <div style={{ width: "30px", height: "30px", border: `3px solid ${colors.border}`, borderTopColor: colors.primary, borderRadius: "50%", animation: "spin 1s linear infinite", display: "inline-block" }}></div>
                                <div style={{ marginTop: "12px", color: colors.gray600 }}>Loading stuck transactions...</div>
                            </div>
                        ) : stuckTxs.length === 0 ? (
                            <div style={{ padding: "40px", textAlign: "center", color: colors.gray500 }}>
                                No stuck transactions detected.
                            </div>
                        ) : (
                            <table className="reconcile-table">
                                <thead>
                                    <tr>
                                        <th>Ref / ID</th>
                                        <th>User ID</th>
                                        <th>Direction</th>
                                        <th>Fiat / Crypto Amount</th>
                                        <th>Poll Attempts</th>
                                        <th>Status</th>
                                        <th>Created At</th>
                                        <th>Override Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stuckTxs.map((tx) => (
                                        <tr key={tx.id}>
                                            <td>
                                                <div style={{ fontWeight: "700" }}>{tx.referenceCode || "N/A"}</div>
                                                <div style={{ fontSize: "11px", color: colors.gray500, fontFamily: "monospace" }}>{tx.id}</div>
                                            </td>
                                            <td style={{ fontWeight: "600" }}>{tx.userId}</td>
                                            <td>
                                                {tx.direction === "ON_RAMP" ? (
                                                    <span className="reconcile-badge" style={{ background: "#e8f4ff", color: colors.info }}>On Ramp</span>
                                                ) : (
                                                    <span className="reconcile-badge" style={{ background: "#fdf6ec", color: colors.accent }}>Off Ramp</span>
                                                )}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: "700" }}>{formatAmount(tx.cryptoAmount)} {tx.cryptoToken}</div>
                                                <div style={{ fontSize: "11px", color: colors.gray600 }}>{formatAmount(tx.fiatAmount)} {tx.fiatCurrency}</div>
                                            </td>
                                            <td style={{ fontWeight: "bold", color: colors.danger }}>{tx.pollAttempts}</td>
                                            <td>
                                                <span className="reconcile-badge" style={{ background: "#fdf6ec", color: colors.accent }}>
                                                    {tx.status}
                                                </span>
                                            </td>
                                            <td>{formatDate(tx.createdAt)}</td>
                                            <td>
                                                {tx.status !== "SETTLED" && tx.status !== "FAILED" && tx.status !== "CANCELLED" && tx.status !== "EXPIRED" && (
                                                    <>
                                                        {tx.direction === "ON_RAMP" && (
                                                            <button
                                                                className="action-btn action-btn-success"
                                                                onClick={() => openActionModal("confirm_deposit", tx)}
                                                            >
                                                                Confirm Deposit
                                                            </button>
                                                        )}
                                                        <button
                                                            className="action-btn action-btn-danger"
                                                            onClick={() => openActionModal("force_resolve", tx)}
                                                        >
                                                            Force Resolve
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {activeTab === "escrow" && (
                    <EscrowTab colors={colors} gradients={gradients} showToast={showToast} />
                )}
            </div>

            {/* Action Modals */}
            {actionModal.isOpen && (
                <div style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center",
                    justifyContent: "center", zIndex: 1000
                }}>
                    <div style={{
                        background: colors.white, borderRadius: "8px", maxWidth: "500px",
                        width: "90%", boxShadow: "0 4px 24px rgba(0,0,0,0.15)", overflow: "hidden"
                    }}>
                        <form onSubmit={handleActionSubmit}>
                            {/* Modal Header */}
                            <div style={{
                                background: actionModal.type === "force_resolve" ? gradients.danger : gradients.success,
                                padding: "16px 20px", color: colors.white
                            }}>
                                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "bold" }}>
                                    {actionModal.type === "force_resolve" ? "Force Resolve Transaction" : "Confirm Wire Deposit"}
                                </h3>
                            </div>

                            {/* Modal Body */}
                            <div style={{ padding: "20px" }}>
                                <div style={{ marginBottom: "16px", fontSize: "14px" }}>
                                    <div style={{ fontWeight: "bold", color: colors.primary }}>Transaction Reference</div>
                                    <div style={{ fontFamily: "monospace", margin: "4px 0", color: colors.gray700 }}>
                                        {actionModal.tx.referenceCode || actionModal.tx.id}
                                    </div>
                                    <div style={{ color: colors.gray600, fontSize: "13px" }}>
                                        Amount: {formatAmount(actionModal.tx.cryptoAmount)} {actionModal.tx.cryptoToken}
                                    </div>
                                </div>

                                {actionModal.type === "force_resolve" ? (
                                    <>
                                        <div style={{ marginBottom: "16px" }}>
                                            <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", color: colors.primary, fontSize: "13px" }}>
                                                Target Resolution Status
                                            </label>
                                            <select
                                                value={resolutionStatus}
                                                onChange={(e) => setResolutionStatus(e.target.value)}
                                                style={{ width: "100%", padding: "10px", borderRadius: "4px", border: `1px solid ${colors.border}`, outline: "none" }}
                                            >
                                                <option value="FAILED">FAILED</option>
                                                <option value="SETTLED">SETTLED</option>
                                            </select>
                                        </div>

                                        <div style={{ marginBottom: "16px" }}>
                                            <label style={{ display: "block", fontWeight: "600", marginBottom: "8px", color: colors.primary, fontSize: "13px" }}>
                                                Justification / Reason (Required)
                                            </label>
                                            <textarea
                                                required
                                                rows="3"
                                                value={actionReason}
                                                onChange={(e) => setActionReason(e.target.value)}
                                                placeholder="Provide audit trail justification for this override..."
                                                style={{ width: "100%", padding: "10px", borderRadius: "4px", border: `1px solid ${colors.border}`, outline: "none", resize: "none", boxSizing: "border-box" }}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ fontSize: "14px", color: colors.gray700, lineHeight: "1.5" }}>
                                        This action will manually verify that the bank wire transfer has settled, immediately trigger Hedera token minting to the user's destination wallet, and transition the transaction status to <strong>SETTLED</strong>.
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div style={{ padding: "16px 20px", background: colors.gray100, display: "flex", justifyContent: "flex-end", borderTop: `1px solid ${colors.border}` }}>
                                <button
                                    type="button"
                                    onClick={closeActionModal}
                                    style={{
                                        padding: "8px 16px", borderRadius: "4px", border: `1px solid ${colors.border}`,
                                        background: colors.white, cursor: "pointer", marginRight: "10px", fontWeight: "600"
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionSubmitting}
                                    style={{
                                        padding: "8px 16px", borderRadius: "4px", border: "none",
                                        background: actionModal.type === "force_resolve" ? colors.danger : colors.success,
                                        color: colors.white, cursor: "pointer", fontWeight: "600",
                                        opacity: actionSubmitting ? 0.6 : 1
                                    }}
                                >
                                    {actionSubmitting ? "Processing..." : "Confirm"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiquidityPoolReconciliation;
